/**
 * The official standings of a tournament linked to TM (tm.fmjd.org).
 *
 * TM ranks; this only displays. The tie-break columns are TM's, in the order
 * TM applies them, and the value that placed a player below the one above on
 * equal points is highlighted, so every place explains itself.
 */

export interface TmStandingsView {
  source: "tm";
  system: string;
  afterRound: number;
  tiebreaks: { key: string; name: string; short: string }[];
  rows: {
    rank: number;
    shared: boolean;
    nickname: string;
    name: string;
    country: string | null;
    score: number;
    tiebreaks: (number | number[])[];
    decidedBy: number | "lot" | "official" | null;
  }[];
}

function value(v: number | number[] | undefined): string {
  if (v === undefined) return "";
  return Array.isArray(v) ? v.join(" / ") : String(v);
}

export function TmStandings({ view, tmUrl }: { view: TmStandingsView; tmUrl?: string }) {
  if (view.rows.length === 0) return <p style={{ color: "#666" }}>No results yet.</p>;
  return (
    <>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
            <th style={th}>#</th>
            <th style={th}>Player</th>
            <th style={th}>Pts</th>
            {view.tiebreaks.map((t) => (
              <th key={t.key} style={{ ...th, textAlign: "right" }} title={t.name}>
                {t.short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {view.rows.map((r, i) => (
            <tr key={`${r.rank}-${r.nickname}`} style={{ borderBottom: "1px solid #eee" }}>
              <td style={td}>{r.shared ? `${r.rank}=` : r.rank}</td>
              <td style={td}>
                {r.nickname}
                {r.country && <span style={{ color: "#888", fontSize: 12 }}> {r.country}</span>}
                {r.decidedBy === "lot" && i > 0 && (
                  <span style={{ color: "#888", fontSize: 12 }} title="Equal on every tie-break: separated by start number">
                    {" "}(start no.)
                  </span>
                )}
                {r.decidedBy === "official" && (
                  <span style={{ color: "#888", fontSize: 12 }} title="Place set officially by the arbiter">
                    {" "}(arbiter)
                  </span>
                )}
              </td>
              <td style={{ ...td, fontWeight: 600 }}>{r.score}</td>
              {view.tiebreaks.map((t, k) => {
                const deciding = r.decidedBy === k;
                return (
                  <td
                    key={t.key}
                    title={deciding ? `Below #${view.rows[i - 1]?.rank} on ${t.name}` : undefined}
                    style={{
                      ...td,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: deciding ? "#00695c" : "#666",
                      fontWeight: deciding ? 700 : 400,
                      background: deciding ? "#e0f2f1" : undefined,
                    }}
                  >
                    {value(r.tiebreaks[k])}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ fontSize: 12.5, color: "#666", marginTop: 10, lineHeight: 1.6 }}>
        Official standings from the FMJD Tournament Manager, after round {view.afterRound}. Ranked by points (win 2,
        draw 1), then: {view.tiebreaks.map((t) => t.name).join(", then ")}. When two players have the same points,
        the highlighted value is the one that separated them.
        {tmUrl && (
          <>
            {" "}
            <a href={tmUrl}>Full tournament on tm.fmjd.org</a>
          </>
        )}
      </p>
    </>
  );
}

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 600 };
const td: React.CSSProperties = { padding: "6px 8px" };
